/**
 * Geometry utility functions for the FloorplanViewer component
 */

/**
 * Calculate polygon area using Shoelace formula
 * @param {Array} points - Array of {x, y} points forming the polygon
 * @param {number} pixelsPerFoot - Scale factor for converting pixel area to square feet
 * @returns {number} Area in square feet
 */
export const calculatePolygonArea = (points, pixelsPerFoot) => {
  if (points.length < 3) return 0;
  
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  
  area = Math.abs(area) / 2;
  // Convert from pixel area to square feet
  return area / (pixelsPerFoot * pixelsPerFoot);
};

/**
 * Snap a line to 90 degree angles (0°, 90°, 180°, 270°)
 * @param {Object} startPoint - Starting point {x, y}
 * @param {Object} endPoint - Current end point {x, y}
 * @returns {Object} New end point snapped to 90 degree increments
 */
export const snapTo90Degrees = (startPoint, endPoint) => {
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  
  // Calculate absolute differences
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  
  // Determine which axis has greater movement
  if (absDx >= absDy) {
    // Snap to horizontal (0° or 180°)
    return { x: endPoint.x, y: startPoint.y };
  } else {
    // Snap to vertical (90° or 270°)
    return { x: startPoint.x, y: endPoint.y };
  }
};

/**
 * Check if a point is close to another point
 * @param {Object} p1 - First point {x, y}
 * @param {Object} p2 - Second point {x, y}
 * @param {number} threshold - Distance threshold in pixels
 * @param {number} scale - Current zoom scale
 * @returns {boolean} True if points are within threshold distance
 */
export const isPointClose = (p1, p2, threshold = 10, scale = 1) => {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy) < threshold / scale;
};

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param {Object} point - Point to check {x, y}
 * @param {Array} polygon - Array of {x, y} points forming the polygon
 * @returns {boolean} True if point is inside polygon
 */
export const isPointInPolygon = (point, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    
    const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

/**
 * Calculate box coordinates for rendering
 * NOTE: Since we're now applying the scale and position transform to the SVG itself,
 * we no longer need to apply it to individual points
 * @param {Object} start - Start point {x, y}
 * @param {Object} end - End point {x, y}
 * @param {number} scale - Current zoom scale (not used with new approach)
 * @param {Object} position - Current pan position {x, y} (not used with new approach)
 * @returns {Object|null} Box coordinates or null if inputs are invalid
 */
export const getBoxCoordinates = (start, end, scale, position) => {
  if (!start || !end) return null;
  
  // Use raw coordinates without scale/position adjustment
  // since the SVG is already transformed
  const x1 = Math.min(start.x, end.x);
  const y1 = Math.min(start.y, end.y);
  const x2 = Math.max(start.x, end.x);
  const y2 = Math.max(start.y, end.y);
  
  return {
    x: x1,
    y: y1,
    width: (x2 - x1),
    height: (y2 - y1),
    centerX: (x1 + x2) / 2,
    centerY: (y1 + y2) / 2
  };
};

/**
 * Transform a point from image coordinates to screen coordinates
 * This keeps elements positioned relative to the floorplan image
 * NOTE: Since we're now applying the scale and position transform to the SVG itself,
 * we no longer need to apply it to individual points
 * @param {Object} point - Point in image coordinates {x, y}
 * @param {number} scale - Current zoom scale (not used with new approach)
 * @param {Object} position - Current pan position {x, y} (not used with new approach)
 * @returns {Object} Point in screen coordinates {x, y}
 */
export const transformPoint = (point, scale, position) => {
  // With the SVG being transformed now, we just return the original coordinates
  return {
    x: point.x,
    y: point.y
  };
}; 